// Shape Lab generator: activities whose answers are computed from the figure that is drawn.

import { int, pick, shuffle, type Rng } from '@kit/rng';

export type Pt = [number, number];

export interface ShapeDef { name: string; sides: number; corners: number; poly: Pt[] | 'circle' | 'oval' }

/** unit-ish polygons centered at the origin, radius ~1 */
function regular(n: number, rot = -Math.PI / 2): Pt[] {
  return Array.from({ length: n }, (_, i) => { const a = rot + i * 2 * Math.PI / n; return [Math.cos(a), Math.sin(a)] as Pt; });
}
export const SHAPES: ShapeDef[] = [
  { name: 'circle', sides: 0, corners: 0, poly: 'circle' },
  { name: 'oval', sides: 0, corners: 0, poly: 'oval' },
  { name: 'triangle', sides: 3, corners: 3, poly: regular(3) },
  { name: 'square', sides: 4, corners: 4, poly: [[-0.8, -0.8], [0.8, -0.8], [0.8, 0.8], [-0.8, 0.8]] },
  { name: 'rectangle', sides: 4, corners: 4, poly: [[-1, -0.55], [1, -0.55], [1, 0.55], [-1, 0.55]] },
  { name: 'rhombus', sides: 4, corners: 4, poly: [[0, -1], [0.7, 0], [0, 1], [-0.7, 0]] },
  { name: 'trapezoid', sides: 4, corners: 4, poly: [[-0.5, -0.6], [0.5, -0.6], [1, 0.6], [-1, 0.6]] },
  { name: 'pentagon', sides: 5, corners: 5, poly: regular(5) },
  { name: 'hexagon', sides: 6, corners: 6, poly: regular(6, 0) },
  { name: 'octagon', sides: 8, corners: 8, poly: regular(8, Math.PI / 8) },
];
/** shapes with at least one mirror line among the 8 candidates (and some with none, for honesty) */
export const SYMMETRY_SHAPES: ShapeDef[] = [
  SHAPES[3], SHAPES[4], SHAPES[5], SHAPES[7], SHAPES[8],
  { name: 'isosceles triangle', sides: 3, corners: 3, poly: [[0, -1], [0.8, 0.8], [-0.8, 0.8]] },
  { name: 'kite', sides: 4, corners: 4, poly: [[0, -1], [0.7, -0.2], [0, 1], [-0.7, -0.2]] },
  { name: 'arrow', sides: 7, corners: 7, poly: [[0, -1], [0.9, 0], [0.4, 0], [0.4, 1], [-0.4, 1], [-0.4, 0], [-0.9, 0]] },
  { name: 'letter T', sides: 8, corners: 8, poly: [[-1, -1], [1, -1], [1, -0.5], [0.25, -0.5], [0.25, 1], [-0.25, 1], [-0.25, -0.5], [-1, -0.5]] },
  { name: 'letter L', sides: 6, corners: 6, poly: [[-0.8, -1], [-0.3, -1], [-0.3, 0.5], [0.8, 0.5], [0.8, 1], [-0.8, 1]] },
];

/** 8 candidate mirror lines through the center, as angles in radians (0 = vertical line) */
export const MIRROR_ANGLES = Array.from({ length: 8 }, (_, i) => i * Math.PI / 8);

/** reflect point across a line through origin with direction angle (measured from the vertical axis) */
export function reflect(p: Pt, angle: number): Pt {
  // line direction d = (sin a, -cos a) so that angle 0 is vertical
  const dx = Math.sin(angle), dy = -Math.cos(angle);
  const dot = p[0] * dx + p[1] * dy;
  return [2 * dot * dx - p[0], 2 * dot * dy - p[1]];
}

/** does reflecting the polygon's vertex set across the line give back the same set? */
export function isMirror(poly: Pt[], angle: number, tol = 0.02): boolean {
  return poly.every(p => { const q = reflect(p, angle); return poly.some(v => Math.hypot(v[0] - q[0], v[1] - q[1]) < tol); });
}
export function mirrorLines(poly: Pt[]): number[] { return MIRROR_ANGLES.filter(a => isMirror(poly, a)); }

export type Activity =
  | { kind: 'name'; shape: ShapeDef; choices: string[]; answer: string }
  | { kind: 'count'; shape: ShapeDef; what: 'sides' | 'corners'; choices: number[]; answer: number }
  | { kind: 'symmetry'; shape: ShapeDef; valid: number[] }
  | { kind: 'angle-sort'; degrees: number; answer: 'smaller than a right angle' | 'a right angle' | 'bigger than a right angle' }
  | { kind: 'perimeter'; cells: Pt[]; answer: number }
  | { kind: 'area-count'; cells: Pt[]; answer: number }
  | { kind: 'area-rect'; w: number; h: number; answer: number }
  | { kind: 'area-tri'; a: number; b: number; answer: number }
  | { kind: 'protractor'; degrees: number; answer: number }
  | { kind: 'plot'; x: number; y: number; size: number }
  | { kind: 'distance'; a: Pt; b: Pt; size: number; answer: number }
  | { kind: 'volume'; l: number; w: number; h: number; answer: number };

export function gridShape(rng: Rng, maxW = 5, maxH = 4): Pt[] {
  // a rectangle, sometimes with a corner bite taken out (an L shape)
  const w = int(rng, 2, maxW), h = int(rng, 2, maxH);
  const cells: Pt[] = [];
  const bite = rng() < 0.5 && w >= 3 && h >= 3;
  const bw = bite ? int(rng, 1, w - 2) : 0, bh = bite ? int(rng, 1, h - 2) : 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { if (bite && x >= w - bw && y >= h - bh) continue; cells.push([x, y]); }
  return cells;
}
export function perimeterOf(cells: Pt[]): number {
  const set = new Set(cells.map(c => c.join(',')));
  let p = 0;
  for (const [x, y] of cells) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (!set.has(`${x + dx},${y + dy}`)) p++;
  return p;
}

export function makeActivity(level: number, rng: Rng): Activity {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) {
    if (rng() < 0.6) {
      const shape = pick(rng, SHAPES);
      const others = shuffle(rng, SHAPES.filter(s => s.name !== shape.name)).slice(0, 3).map(s => s.name);
      return { kind: 'name', shape, choices: shuffle(rng, [shape.name, ...others]), answer: shape.name };
    }
    const shape = pick(rng, SHAPES.filter(s => s.sides > 0));
    const what = rng() < 0.5 ? 'sides' : 'corners';
    const answer = shape[what];
    const choices = shuffle(rng, [...new Set([answer, answer + 1, Math.max(0, answer - 1), answer + 2])]).slice(0, 4);
    return { kind: 'count', shape, what, choices, answer };
  }
  if (L <= 4) {
    if (rng() < 0.5) {
      const shape = pick(rng, SYMMETRY_SHAPES);
      const poly = shape.poly as Pt[];
      return { kind: 'symmetry', shape, valid: mirrorLines(poly) };
    }
    const degrees = pick(rng, [30, 45, 60, 75, 90, 90, 105, 120, 135, 150]);
    return { kind: 'angle-sort', degrees, answer: degrees < 90 ? 'smaller than a right angle' : degrees === 90 ? 'a right angle' : 'bigger than a right angle' };
  }
  if (L <= 6) {
    const cells = gridShape(rng);
    return rng() < 0.5 ? { kind: 'perimeter', cells, answer: perimeterOf(cells) } : { kind: 'area-count', cells, answer: cells.length };
  }
  if (L <= 8) {
    const r = rng();
    if (r < 0.35) { const w = int(rng, 2, 9), h = int(rng, 2, 9); return { kind: 'area-rect', w, h, answer: w * h }; }
    if (r < 0.65) { const a = int(rng, 2, 8) * 2, b = int(rng, 2, 9); return { kind: 'area-tri', a, b, answer: a * b / 2 }; }
    const degrees = int(rng, 2, 17) * 10; return { kind: 'protractor', degrees, answer: degrees };
  }
  const r = rng();
  if (r < 0.35) { return { kind: 'plot', x: int(rng, 0, 6), y: int(rng, 0, 6), size: 6 }; }
  if (r < 0.7) { const a: Pt = [int(rng, 0, 6), int(rng, 0, 6)]; let b: Pt = [int(rng, 0, 6), int(rng, 0, 6)]; if (a[0] === b[0] && a[1] === b[1]) b = [(a[0] + 3) % 7, a[1]]; return { kind: 'distance', a, b, size: 6, answer: Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) }; }
  const l = int(rng, 2, 4), w = int(rng, 2, 4), h = int(rng, 1, 4); return { kind: 'volume', l, w, h, answer: l * w * h };
}

export function activityAnswer(a: Activity): number | string | null {
  switch (a.kind) {
    case 'name': return a.answer;
    case 'count': case 'perimeter': case 'area-count': case 'area-rect': case 'area-tri': case 'protractor': case 'distance': case 'volume': return a.answer;
    case 'angle-sort': return a.answer;
    default: return null;
  }
}
